import { useAtomValue } from "@effect/atom-react";
import type {
  DesktopPreviewConfigInput,
  DesktopPreviewBridge,
  DesktopPreviewWebviewConfig,
  EnvironmentId,
  ProjectId,
} from "@t3tools/contracts";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import { previewBridge } from "~/components/preview/previewBridge";

const PREVIEW_CONFIG_STALE_TIME_MS = 5 * 60_000;
const PREVIEW_CONFIG_IDLE_TTL_MS = 10 * 60_000;

export interface PreviewWebviewConfigTarget {
  readonly environmentId: EnvironmentId;
  /** Stable project identity, when the thread shell is available. */
  readonly projectId: ProjectId | null;
}

const targetLabel = (target: {
  readonly environmentId: string;
  readonly projectId: string | null;
}): string =>
  target.projectId === null
    ? `environment "${target.environmentId}"`
    : `project "${target.projectId}"`;

const toDesktopPreviewConfigInput = (
  target: PreviewWebviewConfigTarget,
): DesktopPreviewConfigInput =>
  target.projectId === null
    ? { environmentId: target.environmentId }
    : { environmentId: target.environmentId, projectId: target.projectId };

export class PreviewWebviewBridgeUnavailableError extends Schema.TaggedErrorClass<PreviewWebviewBridgeUnavailableError>()(
  "PreviewWebviewBridgeUnavailableError",
  {
    environmentId: Schema.String,
    projectId: Schema.NullOr(Schema.String),
  },
) {
  override get message(): string {
    return `Desktop preview configuration is unavailable for ${targetLabel(this)}.`;
  }
}

export class PreviewWebviewConfigLoadError extends Schema.TaggedErrorClass<PreviewWebviewConfigLoadError>()(
  "PreviewWebviewConfigLoadError",
  {
    environmentId: Schema.String,
    projectId: Schema.NullOr(Schema.String),
    cause: Schema.Defect(),
  },
) {
  override get message(): string {
    return `Failed to load desktop preview configuration for ${targetLabel(this)}.`;
  }
}

export const PreviewWebviewConfigError = Schema.Union([
  PreviewWebviewBridgeUnavailableError,
  PreviewWebviewConfigLoadError,
]);
export type PreviewWebviewConfigError = typeof PreviewWebviewConfigError.Type;

type PreviewConfigBridge = Pick<DesktopPreviewBridge, "getPreviewConfig">;

export const loadPreviewWebviewConfig = (
  target: PreviewWebviewConfigTarget,
  bridge: PreviewConfigBridge | null = previewBridge,
): Effect.Effect<DesktopPreviewWebviewConfig, PreviewWebviewConfigError> => {
  if (bridge === null) {
    return Effect.fail(new PreviewWebviewBridgeUnavailableError(target));
  }

  return Effect.tryPromise({
    try: () => bridge.getPreviewConfig(toDesktopPreviewConfigInput(target)),
    catch: (cause) => new PreviewWebviewConfigLoadError({ ...target, cause }),
  });
};

const previewWebviewConfigAtom = Atom.family((environmentId: EnvironmentId) =>
  Atom.family((projectId: ProjectId | null) =>
    Atom.make(loadPreviewWebviewConfig({ environmentId, projectId })).pipe(
      Atom.swr({
        staleTime: PREVIEW_CONFIG_STALE_TIME_MS,
        revalidateOnMount: true,
      }),
      Atom.setIdleTTL(PREVIEW_CONFIG_IDLE_TTL_MS),
      Atom.withLabel(`preview:webview-config:${environmentId}:${projectId ?? "environment"}`),
    ),
  ),
);

export function usePreviewWebviewConfig(
  target: PreviewWebviewConfigTarget,
): DesktopPreviewWebviewConfig | null {
  const result = useAtomValue(previewWebviewConfigAtom(target.environmentId)(target.projectId));
  return Option.getOrNull(AsyncResult.value(result));
}
